import React, { useState, useEffect, useRef, useCallback, Fragment } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { dashboardToast } from "../../utils/dashboardToast";
import "bootstrap/dist/css/bootstrap.min.css";

import Menu from "../components/menu";
import DashboardShell from "../../components/DashboardShell";
import { useMenuState } from "../../Common/MenuStateContext";
import SaListPageShell from "../../components/sa/SaListPageShell";
import ContentLoader from "../../components/ContentLoader";
import RecordDetailModal from "../../components/RecordDetailModal";
import TableSortSelect from "../../components/TableSortSelect";
import { Alert, Button as UiButton } from "../../components/ui";
import { TABLE_SORT } from "../../utils/tableSort";
import { getDataAIValueLevers, addOrUpdateDataAIValueLever, deleteDataAIValueLever } from "../../services/CostEstimationsetupApi";
import { Brain, ChevronDown, ChevronRight, Database, Pencil, Plus, Search, Trash2 } from "lucide-react";
import "../../assets/css/dashboard.css";

const EmptyCell = () => <span className="app-td__empty">—</span>;

const DataManagement = () => {
  const { isMenuCollapsed, toggleMenuCollapse } = useMenuState();
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);
  const [viewMode, setViewMode] = useState("parent"); // "parent" | "child"
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(""); // "dataAI" or "subLever"
  const [expandedRows, setExpandedRows] = useState(new Set()); // Track expanded data AI rows
  const [isMounted, setIsMounted] = useState(true);

  // Data AI Value Levers form data
  const [dataAIForm, setDataAIForm] = useState({
    id: 0,
    name: "",
    subLever: "",
    description: "",
    isActive: true,
    subLevers: [] // New state for sub-levers
  });

  // Sub Lever form data
  const [subLeverForm, setSubLeverForm] = useState({
    id: 0,
    dataAIId: "",
    name: "",
    subLever: "",
    description: "",
    isActive: true
  });

  // Data AI Value Levers from API
  const [dataAILevers, setDataAILevers] = useState([]);

  // Sub Levers from API (grouped by main lever)
  const [subLevers, setSubLevers] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState(TABLE_SORT.DATE_DESC);

  // Safe toast function that checks if component is mounted
  const safeToast = {
    success: (message) => {
      if (isMounted) {
        dashboardToast.success(message);
      }
    },
    error: (message) => {
      if (isMounted) {
        dashboardToast.error(message);
      }
    },
    info: (message) => {
      if (isMounted) {
        dashboardToast.info(message);
      }
    }
  };

  const handleMenuSelection = useCallback((item) => {
    setSelectedItem(item);
    return item;
  }, []);

  // Get sub levers for a specific Data AI Value Lever
  const getSubLevers = useCallback((dataAIId) => {
    return subLevers.filter(sl => sl.dataAIId === dataAIId);
  }, [subLevers]);

  // Function to group data by main lever names
  const groupDataByMainLever = useCallback((dataAILeversData) => {
    const mainLevers = [];

    dataAILeversData.forEach(item => {
      // Create main lever entry
      const mainLever = {
        id: item.id,
        name: item.name,
        subLever: "",
        description: item.description,
        isActive: item.isActive,
        subLevers: item.subLevers || []
      };
      mainLevers.push(mainLever);
    });

    return mainLevers;
  }, []);

  // Function to fetch Data AI Value Levers from API
  const fetchDataAIValueLevers = useCallback(async (searchTerm = '', isActive = true, sort = sortKey) => {
    try {
      const response = await getDataAIValueLevers({
        searchTerm,
        isActive,
        page: 1,
        pageSize: 100,
        sort,
      });

      // Handle the response structure: { data: { data: [...], totalRecords: ..., pageNumber: ..., pageSize: ..., totalPages: ..., hasNextPage: ..., hasPreviousPage: ... }, succeeded: true, code: 0, message: "" }
      if (response && response.data && response.data.data) {
        const dataAILeversData = response.data.data.map(item => ({
          id: item.id,
          name: item.name,
          subLever: "", // Main lever has empty subLever
          description: item.description || "",
          isActive: item.isActive,
          subLevers: item.dataAIValueSubLevers ? item.dataAIValueSubLevers.map(subLever => ({
            id: subLever.id,
            dataAIId: subLever.dataAIValueLeverId,
            name: subLever.name,
            subLever: subLever.name,
            description: subLever.description || "",
            isActive: subLever.isActive
          })) : []
        }));

        return dataAILeversData;
      } else if (response && response.data && Array.isArray(response.data)) {
        // Fallback for different response structure
        return response.data;
      } else if (Array.isArray(response)) {
        // Direct array response
        return response;
      }

      console.warn('Unexpected response structure:', response);
      return [];
    } catch (error) {
      console.error('Error fetching Data AI Value Levers from API:', error);
      safeToast.error(`Failed to fetch Data AI Value Levers from server: ${error.message}`);
      return [];
    }
  }, [safeToast, sortKey]);

  // Helper function to refresh data and update state
  const refreshData = useCallback(async (searchTermParam = searchTerm, showSuccessMessage = false, manageLoading = true, sort = sortKey) => {
    try {
      if (manageLoading) {
        setLoading(true);
      }

      const dataAILeversData = await fetchDataAIValueLevers(searchTermParam, true, sort);
      const groupedData = groupDataByMainLever(dataAILeversData);
      setDataAILevers(groupedData);

      // Extract all sub-levers
      const allSubLevers = [];
      groupedData.forEach(mainLever => {
        if (mainLever.subLevers && mainLever.subLevers.length > 0) {
          allSubLevers.push(...mainLever.subLevers);
        }
      });
      setSubLevers(allSubLevers);

      if (showSuccessMessage) {
        safeToast.success('Data refreshed successfully');
      }
    } catch (error) {
      console.error('Error refreshing data:', error);
      safeToast.error('Failed to refresh data');
    } finally {
      if (manageLoading) {
        setLoading(false);
      }
    }
  }, [fetchDataAIValueLevers, groupDataByMainLever, searchTerm, safeToast, sortKey]);

  // Data AI Value Levers handlers
  const handleShowDataAI = useCallback((dataAI = null) => {
    // Clear sub-lever form when showing Data + AI form
    setSubLeverForm({
      id: 0,
      dataAIId: "",
      name: "",
      subLever: "",
      description: "",
      isActive: true
    });

    if (dataAI) {
      // Get existing sub-levers for this Data AI Value Lever
      const existingSubLevers = (dataAI.subLevers || []).map(subLever => ({
        id: subLever.id,
        name: subLever.name,
        description: subLever.description || "",
        isActive: subLever.isActive
      }));

      setDataAIForm({
        id: dataAI.id,
        name: dataAI.name || "",
        subLever: dataAI.subLever || "",
        description: dataAI.description || "",
        isActive: dataAI.isActive !== undefined ? dataAI.isActive : true,
        subLevers: existingSubLevers // Load existing sub-levers
      });
    } else {
      setDataAIForm({
        id: 0,
        name: "",
        subLever: "",
        description: "",
        isActive: true,
        subLevers: []
      });
    }
    setShowModal(true);
  }, []);

  // Sub Levers handlers
  const handleShowSubLever = useCallback((subLever = null) => {
    // Clear Data + AI form when showing sub-lever form
    setDataAIForm({
      id: 0,
      name: "",
      subLever: "",
      description: "",
      isActive: true
    });

    if (subLever && subLever.id) {
      // Editing existing sub-lever
      setSubLeverForm({
        id: subLever.id,
        dataAIId: subLever.dataAIId || "",
        name: subLever.name || "",
        subLever: subLever.subLever || "",
        description: subLever.description || "",
        isActive: subLever.isActive !== undefined ? subLever.isActive : true
      });
    } else if (subLever && subLever.dataAIId) {
      // Creating new sub-lever for specific Data + AI Value Lever
      setSubLeverForm({
        id: 0,
        dataAIId: subLever.dataAIId,
        name: "",
        subLever: "",
        description: "",
        isActive: true
      });
    } else {
      // Creating new sub-lever (no pre-selected Data + AI Value Lever)
      setSubLeverForm({
        id: 0,
        dataAIId: "",
        name: "",
        subLever: "",
        description: "",
        isActive: true
      });
    }
    setShowModal(true);
  }, []);

  const handleClose = useCallback(() => {
    setShowModal(false);
    // Reset both forms when closing modal
    setDataAIForm({
      id: 0,
      name: "",
      subLever: "",
      description: "",
      isActive: true,
      subLevers: []
    });
    setSubLeverForm({
      id: 0,
      dataAIId: "",
      name: "",
      subLever: "",
      description: "",
      isActive: true
    });
  }, []);

  const handleDataAIChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setDataAIForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  const handleSubLeverChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setSubLeverForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  const handleAddSubLeverField = useCallback(() => {
    setDataAIForm(prev => ({
      ...prev,
      subLevers: [...prev.subLevers, { name: "", description: "", isActive: true }]
    }));
  }, []);

  const handleRemoveSubLeverField = useCallback(async (index) => {
    const subLeverToRemove = dataAIForm.subLevers[index];
    
    // If it's an existing sub-lever (has an ID), delete it from the server
    if (subLeverToRemove.id && subLeverToRemove.id > 0) {
      try {
        await deleteDataAIValueLever(subLeverToRemove.id, true); // isSubLever = true
        safeToast.success("Sub Data deleted successfully");
        
        // Refresh data to get updated list
        await refreshData('', false, false);
      } catch (err) {
        console.error('Error deleting sub-lever:', err);
        safeToast.error(`Failed to delete Sub Data: ${err.message || 'Unknown error'}`);
        return; // Don't remove from form if API call failed
      }
    }
    
    // Remove from form state (for both new and existing sub-levers)
    setDataAIForm(prev => {
      const newSubLevers = [...prev.subLevers];
      newSubLevers.splice(index, 1);
      return { ...prev, subLevers: newSubLevers };
    });
  }, [dataAIForm.subLevers, safeToast, refreshData]);

  const handleSubLeverFieldChange = useCallback((index, field, value) => {
    setDataAIForm(prev => ({
      ...prev,
      subLevers: prev.subLevers.map((subLever, i) =>
        i === index ? { ...subLever, [field]: value } : subLever
      )
    }));
  }, []);

  const handleDataAISubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const { name, description, subLevers } = dataAIForm;
      
      if (name) {
        try {
          // Prepare data for API
          const apiData = {
            id: dataAIForm.id || 0,
            name: name.trim(),
            description: description || "",
            isActive: dataAIForm.isActive,
            dataAIValueSubLevers: (subLevers || []).map(subLever => ({
              id: subLever.id || 0,
              dataAIValueLeverId: dataAIForm.id || 0,
              name: subLever.name.trim(),
              description: subLever.description || "",
              isActive: subLever.isActive !== undefined ? subLever.isActive : true
            }))
          };

          await addOrUpdateDataAIValueLever(apiData);

          if (dataAIForm.id === 0) {
            safeToast.success("Data + AI Value Lever created successfully");
          } else {
            safeToast.success("Data + AI Value Lever updated successfully");
          }

          // Refresh data after successful operation
          await refreshData('', false, false);
          handleClose();
        } catch (err) {
          console.error('Error in handleDataAISubmit:', err);
          safeToast.error(`Failed to save Data + AI Value Lever: ${err.message || 'Unknown error'}`);
        }
      } else {
        safeToast.error("Please fill in all required fields");
      }
    },
    [dataAIForm, handleClose, refreshData, safeToast]
  );

  const handleSubLeverSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const { dataAIId, name, subLever, description } = subLeverForm;
      if (dataAIId && name && subLever) {
        try {
          if (subLeverForm.id === 0) {
            // Create new Sub Lever
            const newSubLever = {
              id: Date.now(),
              dataAIId: parseInt(dataAIId),
              name: name,
              subLever: subLever.toUpperCase(),
              description: description,
              isActive: subLeverForm.isActive
            };
            setSubLevers(prev => [...prev, newSubLever]);
            safeToast.success("Sub Data created successfully");
          } else {
            // Update existing Sub Lever
            setSubLevers(prev =>
              prev.map(sl =>
                sl.id === subLeverForm.id
                  ? {
                    ...sl,
                    dataAIId: parseInt(dataAIId),
                    name,
                    subLever: subLever.toUpperCase(),
                    description,
                    isActive: subLeverForm.isActive
                  }
                  : sl
              )
            );
            safeToast.success("Sub Data updated successfully");
          }
          handleClose();
        } catch (err) {
          console.error(err);
          safeToast.error("Failed to save Sub Data");
        }
      } else {
        safeToast.error("Please fill in all required fields");
      }
    },
    [subLeverForm, handleClose, safeToast]
  );

  const handleDelete = useCallback((item, type) => {
    setItemToDelete(item);
    setDeleteType(type);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    try {
      if (deleteType === "dataAI") {
        // Delete Data AI Value Lever (main lever)
        if (itemToDelete.id) {
          const idToDelete = parseInt(itemToDelete.id);
          if (!isNaN(idToDelete) && idToDelete > 0) {
            await deleteDataAIValueLever(idToDelete, false); // isSubLever = false for main lever
          } else {
            console.error('Invalid ID for deletion:', itemToDelete.id);
            safeToast.error('Invalid ID for deletion');
            return;
          }
        }

        safeToast.success("Data + AI Value Lever deleted successfully");
        
        // Refresh data after successful deletion
        await refreshData('', false, false);
      }
    } catch (err) {
      console.error('Error in confirmDelete:', err);
      safeToast.error(`Failed to delete item: ${err.message || 'Unknown error'}`);
    } finally {
      setShowDeleteModal(false);
      setItemToDelete(null);
      setDeleteType("");
    }
  }, [itemToDelete, deleteType, safeToast, refreshData]);

  const cancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setItemToDelete(null);
    setDeleteType("");
  }, []);

  const openViewModal = useCallback((record, mode = "parent") => {
    setViewRecord(record);
    setViewMode(mode);
    setShowViewModal(true);
  }, []);

  const closeViewModal = useCallback(() => {
    setShowViewModal(false);
    setViewRecord(null);
    setViewMode("parent");
  }, []);

  // Toggle row expansion
  const toggleRowExpansion = (dataAIId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dataAIId)) {
        newSet.delete(dataAIId);
      } else {
        newSet.add(dataAIId);
      }
      return newSet;
    });
  };

  // Get sub levers count for a Data AI Value Lever
  const getSubLeversCount = (dataAIId) => {
    return subLevers.filter(sl => sl.dataAIId === dataAIId).length;
  };

  // Load initial data when component mounts
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        await refreshData('', false, true);
      } catch (error) {
        console.error('Error loading initial data:', error);
        setError('Failed to load Data AI Value Levers');
      }
    };

    loadInitialData();
  }, []); // Empty dependency array to run only once on mount

  // Auto-search when search term changes (with debounce)
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      try {
        await refreshData(searchTerm, false, true);
      } catch (error) {
        console.error('Error searching data:', error);
        safeToast.error('Failed to search Data AI Value Levers');
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm, sortKey]); // Search and sort refetch the current page

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      setIsMounted(false);
    };
  }, []);



  // Get all Data AI Value Levers
  const getAllDataAILevers = () => {
    return dataAILevers;
  };

  return (
    <DashboardShell
      isMenuCollapsed={isMenuCollapsed}
      sidebar={
        <Menu
          onSelect={handleMenuSelection}
          selectedItem={selectedItem}
          isCollapsed={isMenuCollapsed}
          onToggleCollapse={toggleMenuCollapse}
        />
      }
    >
      <SaListPageShell topBarTitle="Data + AI Management">
        <div className="dash-frame-parent1">
          <div className="dash-list-toolbar">
            <div className="dash-list-toolbar__intro">
              <h5 className="dash-list-toolbar__title">Manage Data + AI Value Levers</h5>
              <p className="dash-list-toolbar__desc">
                Create and manage Data + AI value levers and their sub-levers.
              </p>
            </div>
            <div className="dash-list-toolbar__actions">
              <div className="dash-search-field dash-table-search">
                <Search className="dash-search-field__icon" aria-hidden />
                <input
                  type="text"
                  placeholder="Search by name…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="dash-search-field__input"
                  disabled={loading}
                />
              </div>
              <TableSortSelect variant="chip"
                value={sortKey}
                onChange={setSortKey}
                disabled={loading}
              />
              <UiButton variant="primary" className="sa-create-btn" onClick={() => handleShowDataAI()}>
                Create Lever
              </UiButton>
            </div>
          </div>

          {loading ? (
            <ContentLoader label="Loading Data + AI value levers…" />
          ) : error ? (
            <Alert variant="error">{error}</Alert>
          ) : (
            <Fragment>
              <div className="app-table-wrap app-table-wrap--refined">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th aria-label="Expand" />
                      <th>#</th>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getAllDataAILevers().length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center">
                          <span className="app-td__desc">No Data + AI value levers found.</span>
                        </td>
                      </tr>
                    ) : (
                      getAllDataAILevers().map((dataAI, index) => {
                        const subLeversList = getSubLevers(dataAI.id);
                        const isExpanded = expandedRows.has(dataAI.id);
                        const displayName = dataAI.name || "Untitled lever";

                        return (
                          <Fragment key={dataAI.id}>
                            <tr
                              className="rdm-row-clickable"
                              onClick={() => openViewModal(dataAI, "parent")}
                            >
                              <td onClick={(e) => e.stopPropagation()}>
                                {subLeversList.length > 0 ? (
                                  <button
                                    type="button"
                                    className="vd-expand-btn"
                                    onClick={() => toggleRowExpansion(dataAI.id)}
                                    title={isExpanded ? "Collapse sub-levers" : "Expand sub-levers"}
                                    aria-expanded={isExpanded}
                                    aria-label={
                                      isExpanded
                                        ? `Collapse ${displayName}`
                                        : `Expand ${displayName}`
                                    }
                                  >
                                    {isExpanded ? <ChevronDown aria-hidden /> : <ChevronRight aria-hidden />}
                                  </button>
                                ) : null}
                              </td>
                              <td>
                                <span className="app-td__index">{index + 1}</span>
                              </td>
                              <td>
                                <span className="app-td__title">{displayName}</span>
                              </td>
                              <td>
                                {dataAI.description ? (
                                  <span className="app-td__desc" title={dataAI.description}>
                                    {dataAI.description}
                                  </span>
                                ) : (
                                  <EmptyCell />
                                )}
                              </td>
                              <td>
                                <span className="app-td__meta">Value lever</span>
                              </td>
                              <td>
                                <span
                                  className={
                                    dataAI.isActive
                                      ? "app-status app-status--success"
                                      : "app-status app-status--muted"
                                  }
                                >
                                  {dataAI.isActive ? "Active" : "Inactive"}
                                </span>
                              </td>
                              <td
                                className="dash-mobile-table-actions"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="btn-list-actions">
                                  <UiButton
                                    variant="outline"
                                    size="sm"
                                    className="btn-list-edit"
                                    title="Edit"
                                    aria-label={`Edit ${displayName}`}
                                    onClick={() => handleShowDataAI(dataAI)}
                                  >
                                    <Pencil />
                                  </UiButton>
                                  <UiButton
                                    variant="danger"
                                    size="sm"
                                    className="btn-list-delete"
                                    title="Delete"
                                    aria-label={`Delete ${displayName}`}
                                    onClick={() => handleDelete(dataAI, "dataAI")}
                                  >
                                    <Trash2 />
                                  </UiButton>
                                </div>
                              </td>
                            </tr>

                            {isExpanded ? (
                              subLeversList.length > 0 ? (
                                subLeversList.map((subLever) => (
                                  <tr
                                    key={`sub-${subLever.id}`}
                                    className="rdm-row-clickable vd-sub-row"
                                    onClick={() => openViewModal(subLever, "child")}
                                  >
                                    <td />
                                    <td />
                                    <td>
                                      <span className="app-td__desc vd-sub-name">
                                        {subLever.name}
                                      </span>
                                    </td>
                                    <td>
                                      {subLever.description ? (
                                        <span className="app-td__desc" title={subLever.description}>
                                          {subLever.description}
                                        </span>
                                      ) : (
                                        <EmptyCell />
                                      )}
                                    </td>
                                    <td>
                                      <span className="app-td__meta">Sub-lever</span>
                                    </td>
                                    <td>
                                      <span
                                        className={
                                          subLever.isActive
                                            ? "app-status app-status--success"
                                            : "app-status app-status--muted"
                                        }
                                      >
                                        {subLever.isActive ? "Active" : "Inactive"}
                                      </span>
                                    </td>
                                    <td>
                                      <EmptyCell />
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr className="vd-sub-row">
                                  <td colSpan={7} className="text-center">
                                    <span className="app-td__desc">No sub-levers yet.</span>
                                  </td>
                                </tr>
                              )
                            ) : null}
                          </Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </Fragment>
          )}
        </div>
      </SaListPageShell>

      {/* Create/Edit Modal */}
      <Modal
        show={showModal}
        onHide={handleClose}
        centered
        size="lg"
        dialogClassName="rounded-3"
      >
        <Modal.Header closeButton className="dashboard-modal-header">
          {dataAIForm.id > 0 ? (
            <>
              <Database className="h-5 w-5 me-2" />
              <Modal.Title>Edit Data + AI Value Lever</Modal.Title>
            </>
          ) : subLeverForm.id > 0 ? (
            <>
              <Brain className="h-5 w-5 me-2" />
              <Modal.Title>Edit Sub Data</Modal.Title>
            </>
          ) : subLeverForm.dataAIId ? (
            <>
              <Brain className="h-5 w-5 me-2" />
              <Modal.Title>Add Sub Data to {dataAILevers.find(dai => dai.id === parseInt(subLeverForm.dataAIId))?.name || 'Data + AI Value Lever'}</Modal.Title>
            </>
          ) : (
            <>
              <Database className="h-5 w-5 me-2" />
              <Modal.Title>Create Data + AI Value Lever</Modal.Title>
            </>
          )}
        </Modal.Header>

        {dataAIForm.id > 0 ? (
          <Form onSubmit={handleDataAISubmit}>
            <Modal.Body className="px-4">
              <Row>
                <Col md={6}>
                  <Form.Group controlId="formName" className="mb-2">
                    <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={dataAIForm.name}
                      onChange={handleDataAIChange}
                      required
                      placeholder="Enter Data + AI Value Lever name"
                      maxLength="100"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="formDescription" className="mb-2">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      type="text"
                      name="description"
                      value={dataAIForm.description}
                      onChange={handleDataAIChange}
                      placeholder="Enter description"
                      maxLength="500"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group controlId="formIsActive" className="mb-0">
                    <Form.Label>Status</Form.Label>
                    <div className="mt-0">
                      <Form.Check
                        type="checkbox"
                        name="isActive"
                        checked={dataAIForm.isActive}
                        onChange={handleDataAIChange}
                        label="Active"
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              {/* Sub Data Management Section */}
              <div className="d-flex justify-content-between align-items-center mt-2 mb-1">
                <h6 className="mb-0">
                  <Brain className="me-2" />
                  Manage Sub Data
                </h6>
                <Button
                  onClick={() => handleAddSubLeverField()}
                  className="btn btn-dashboard-primary"
                  size="sm"
                >
                  <Plus className="me-1" />
                  Add More
                </Button>
              </div>

              {/* Existing Sub Data (Editable when editing) */}
              {dataAIForm.id > 0 && dataAIForm.subLevers && dataAIForm.subLevers.length > 0 && (
                <div className="mb-3">
                  {/* Editable Sub Data Fields */}
                  {dataAIForm.subLevers.map((subLever, index) => (
                    <div key={subLever.id || index} className="border rounded p-2 mb-2 dashboard-form-section">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">
                          Sub Data {index + 1}
                        </h6>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="cap-mgmt-icon-btn"
                          onClick={() => handleRemoveSubLeverField(index)}
                        >
                          <Trash2 />
                        </Button>
                      </div>
                      <Row>
                        <Col md={6}>
                          <Form.Group controlId={`subLeverName${index}`} className="mb-2">
                            <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                              type="text"
                              value={subLever.name}
                              onChange={(e) => handleSubLeverFieldChange(index, 'name', e.target.value)}
                              placeholder="Enter sub data name"
                              maxLength="100"
                              style={{ height: '38px' }}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group controlId={`subLeverStatus${index}`} className="mb-2">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                              value={subLever.isActive ? 'true' : 'false'}
                              onChange={(e) => handleSubLeverFieldChange(index, 'isActive', e.target.value === 'true')}
                              style={{ height: '38px' }}
                            >
                              <option value="true">Active</option>
                              <option value="false">Inactive</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={12}>
                          <Form.Group controlId={`subLeverDescription${index}`} className="mb-0">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              value={subLever.description}
                              onChange={(e) => handleSubLeverFieldChange(index, 'description', e.target.value)}
                              placeholder="Enter description"
                              maxLength="500"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>
                  ))}
                </div>
              )}

              {/* Sub Data Creation Section (Only for new Data AI Value Levers) */}
              {dataAIForm.id === 0 && (
                <>
                  <div className="d-flex justify-content-between align-items-center mt-2 mb-1">
                    <h6 className="mb-0">
                      <Brain className="me-2" />
                      Add Sub Data
                    </h6>
                    <Button
                      onClick={() => handleAddSubLeverField()}
                      className="btn btn-dashboard-primary"
                      size="sm"
                    >
                      <Plus className="me-1" />
                      Add More
                    </Button>
                  </div>

                  {/* Sub Data Fields */}
                  {dataAIForm.subLevers && dataAIForm.subLevers.map((subLever, index) => (
                    <div key={index} className="border rounded p-1 mb-1" style={{ backgroundColor: "#f8f9fa" }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <h6 className="mb-0">Sub Data {index + 1}</h6>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="cap-mgmt-icon-btn"
                          onClick={() => handleRemoveSubLeverField(index)}
                        >
                          <Trash2 />
                        </Button>
                      </div>
                      <Row>
                        <Col md={6}>
                          <Form.Group controlId={`subLeverName${index}`} className="mb-1">
                            <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                              type="text"
                              value={subLever.name}
                              onChange={(e) => handleSubLeverFieldChange(index, 'name', e.target.value)}
                              placeholder="Enter sub data name"
                              maxLength="100"
                              style={{ height: '38px' }}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group controlId={`subLeverStatus${index}`} className="mb-1">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                              value={subLever.isActive !== undefined ? (subLever.isActive ? 'true' : 'false') : 'true'}
                              onChange={(e) => handleSubLeverFieldChange(index, 'isActive', e.target.value === 'true')}
                              style={{ height: '38px' }}
                            >
                              <option value="true">Active</option>
                              <option value="false">Inactive</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={2}>
                          <Form.Group className="mb-1">
                            <Form.Label>&nbsp;</Form.Label>
                            <div className="d-flex align-items-center" style={{ height: '38px' }}>
                              <span className={`badge ${subLever.isActive !== undefined ? (subLever.isActive ? 'bg-success' : 'bg-secondary') : 'bg-success'}`}>
                                {subLever.isActive !== undefined ? (subLever.isActive ? 'Active' : 'Inactive') : 'Active'}
                              </span>
                            </div>
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={12}>
                          <Form.Group controlId={`subLeverDescription${index}`} className="mb-0">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={1}
                              value={subLever.description}
                              onChange={(e) => handleSubLeverFieldChange(index, 'description', e.target.value)}
                              placeholder="Enter description"
                              maxLength="500"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>
                  ))}
                </>
              )}
            </Modal.Body>

            <Modal.Footer>
              <Button variant="outline-secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="btn btn-dashboard-primary"
              >
                Save Data + AI Value Lever
              </Button>
            </Modal.Footer>
          </Form>
        ) : (
          <Form onSubmit={handleDataAISubmit}>
            <Modal.Body className="px-4">
              <Row>
                <Col md={6}>
                  <Form.Group controlId="formName" className="mb-2">
                    <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={dataAIForm.name}
                      onChange={handleDataAIChange}
                      required
                      placeholder="Enter Data + AI Value Lever name"
                      maxLength="100"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="formDescription" className="mb-2">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      type="text"
                      name="description"
                      value={dataAIForm.description}
                      onChange={handleDataAIChange}
                      placeholder="Enter description"
                      maxLength="500"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group controlId="formIsActive" className="mb-0">
                    <Form.Label>Status</Form.Label>
                    <div className="mt-0">
                      <Form.Check
                        type="checkbox"
                        name="isActive"
                        checked={dataAIForm.isActive}
                        onChange={handleDataAIChange}
                        label="Active"
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              {/* Sub Lever Creation Section */}
              <div className="d-flex justify-content-between align-items-center mt-2 mb-1">
                <h6 className="mb-0">
                  <Brain className="me-2" />
                  Add Sub Data
                </h6>
                <Button
                  onClick={() => handleAddSubLeverField()}
                  className="btn btn-dashboard-primary"
                  size="sm"
                >
                  <Plus className="me-1" />
                  Add More
                </Button>
              </div>

              {/* Sub Lever Fields */}
              {dataAIForm.subLevers && dataAIForm.subLevers.map((subLever, index) => (
                <div key={index} className="border rounded p-1 mb-1" style={{ backgroundColor: "#f8f9fa" }}>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <h6 className="mb-0">Sub Data {index + 1}</h6>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="cap-mgmt-icon-btn"
                      onClick={() => handleRemoveSubLeverField(index)}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                  <Row>
                    <Col md={6}>
                      <Form.Group controlId={`subLeverName${index}`} className="mb-1">
                        <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          value={subLever.name}
                          onChange={(e) => handleSubLeverFieldChange(index, 'name', e.target.value)}
                          placeholder="Enter sub data name"
                          maxLength="100"
                          style={{ height: '38px' }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group controlId={`subLeverStatus${index}`} className="mb-1">
                        <Form.Label>Status</Form.Label>
                        <Form.Select
                          value={subLever.isActive !== undefined ? (subLever.isActive ? 'true' : 'false') : 'true'}
                          onChange={(e) => handleSubLeverFieldChange(index, 'isActive', e.target.value === 'true')}
                          style={{ height: '38px' }}
                        >
                          <option value="true">Active</option>
                          <option value="false">Inactive</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-1">
                        <Form.Label>&nbsp;</Form.Label>
                        <div className="d-flex align-items-center" style={{ height: '38px' }}>
                          <span className={`badge ${subLever.isActive !== undefined ? (subLever.isActive ? 'bg-success' : 'bg-secondary') : 'bg-success'}`}>
                            {subLever.isActive !== undefined ? (subLever.isActive ? 'Active' : 'Inactive') : 'Active'}
                          </span>
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <Form.Group controlId={`subLeverDescription${index}`} className="mb-0">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={1}
                          value={subLever.description}
                          onChange={(e) => handleSubLeverFieldChange(index, 'description', e.target.value)}
                          placeholder="Enter description"
                          maxLength="500"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              ))}
            </Modal.Body>

            <Modal.Footer>
              <Button variant="outline-secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="btn btn-dashboard-primary"
              >
                Save Data + AI Value Lever
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal>

      {/* Delete Confirmation */}
      <Modal show={showDeleteModal} onHide={cancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete{" "}
          <strong>{itemToDelete?.name}</strong>?
          {deleteType === "dataAI" && (
            <div className="alert alert-warning mt-2">
              <strong>Warning:</strong> This will also delete all related Sub Data.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelDelete}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <RecordDetailModal
        show={showViewModal}
        onHide={closeViewModal}
        title={
          viewRecord?.name ||
          (viewMode === "child" ? "Sub Data Details" : "Data + AI Value Lever Details")
        }
        subtitle={
          viewMode === "child" ? "Sub data record" : "Data + AI value lever record"
        }
        fields={
          viewMode === "child"
            ? [
                { label: "Name", value: viewRecord?.name },
                {
                  label: "Description",
                  value: viewRecord?.description,
                  fullWidth: true,
                },
                { label: "Type", value: "Sub Data" },
                { label: "Active", value: Boolean(viewRecord?.isActive) },
              ]
            : [
                { label: "Name", value: viewRecord?.name },
                {
                  label: "Description",
                  value: viewRecord?.description,
                  fullWidth: true,
                },
                { label: "Type", value: "Data + AI Value Lever" },
                { label: "Active", value: Boolean(viewRecord?.isActive) },
              ]
        }
        groups={
          viewMode === "parent"
            ? [
                {
                  title: "Sub Data",
                  content: (() => {
                    const children = viewRecord
                      ? getSubLevers(viewRecord.id)
                      : [];
                    if (!children.length) {
                      return <span className="rdm-empty">None</span>;
                    }
                    return children.map((sub) => (
                      <div key={sub.id} className="rdm-nested-card">
                        <div className="rdm-nested-card-title">{sub.name}</div>
                        {sub.description ? (
                          <div className="rdm-nested-meta">{sub.description}</div>
                        ) : null}
                        <div className="rdm-nested-meta">
                          {sub.isActive ? "Active" : "Inactive"}
                        </div>
                      </div>
                    ));
                  })(),
                },
              ]
            : []
        }
        actions={
          viewMode === "parent"
            ? [
                {
                  type: "edit",
                  onClick: () => {
                    const record = viewRecord;
                    closeViewModal();
                    if (record) handleShowDataAI(record);
                  },
                },
                {
                  type: "delete",
                  onClick: () => {
                    const record = viewRecord;
                    closeViewModal();
                    if (record) handleDelete(record, "dataAI");
                  },
                },
              ]
            : []
        }
      />

    </DashboardShell>
  );
};

export default DataManagement;
